/**
 * ScrollManager — single page-wide `scroll` listener.
 *
 * Front-end components (e.g. parallax) that need to react to scroll should
 * subscribe here instead of adding their own `window.addEventListener( 'scroll', ... )`.
 * That way N components stacked on one page still cost a single native
 * listener and a single rAF-batched dispatch per frame, not N of each.
 *
 * Lives in `front-scripts.js` (bundled into the site-wide main.bundle.js) and
 * is exposed on `window.wombat.scrollManager` so per-block bundles — which are
 * built as separate webpack entries with no shared chunk — can subscribe
 * without each pulling in (and instantiating) their own copy of this module.
 */
class ScrollManager {

	/**
	 * Attach the single page-wide scroll listener.
	 */
	constructor() {

		this.listeners = new Set();
		this.rafId = null;
		window.addEventListener( 'scroll', this.onScroll, { passive: true } );

	}

	onScroll = () => {

		if ( this.rafId ) {

			return;

		}

		this.rafId = window.requestAnimationFrame( () => {

			this.rafId = null;
			this.listeners.forEach( ( listener ) => listener() );

		} );

	};

	/**
	 * Subscribe to scroll updates.
	 *
	 * @param {Function} callback Called (no args) on the next frame after a scroll.
	 * @return {Function} Unsubscribe function.
	 */
	addListener = ( callback ) => {

		this.listeners.add( callback );

		return () => this.removeListener( callback );

	};

	removeListener = ( callback ) => {

		this.listeners.delete( callback );

	};

}

export default new ScrollManager();
